import React, { memo, useContext } from 'react'
import { Header, ScreenContext } from 'react-components'

import type { StyledSvgComponent } from '../../../types/svg'
import {
    GhostButtonContent,
    GhostButtonWrapper,
    HeaderBar,
    HeaderBarContent,
    IconImage,
    LeftSubContainer,
    ListRentalWrapper,
    MenuIconWrapper,
    RightSubContainer,
    SearchIconContainer,
    StickyContainer,
    SeoHref,
    CompactLayoutWrapper,
    DesktopLayoutWrapper,
    UserActionsContainer,
} from './header-section-styles'
import { HeaderSectionTransport } from '../../../transports/region-landing-page-transport'
import MenuDrawer from './menu-drawer'
import ProfileDrawer from './profile-drawer'
import { MegabarTransport } from '../../../transports/common/megabar-transport'
import GhostButton from '../../common/ghost-button'
import SearchIcon from '../../../assets/icons/userbar-icons/search-grey.svg'
import NavigationIcon from '../../../assets/icons/userbar-icons/navigation-grey.svg'
import LogoArea from './logo-area'
import SearchArea from './search-area'
import NavigationLinks from './navigation-links'
import WishlistButton from './wishlist-button'
import UserMenuDesktop from './user-menu-desktop'
import UserMenuMobile from './user-menu-mobile'
import { sanitizeUrlPath } from '../../landing-page/utils'
import { useLandingPageHeader } from '../../region-landing-page/hooks'

interface Props {
    isLoggedIn: boolean
    firstName?: string
    url: string
    headerSection: HeaderSectionTransport
    showVacationIdeasNavButton: boolean
    onSearchIconClick?: React.MouseEventHandler<HTMLDivElement>
    isAllInputModalOpen?: boolean
    hideListRental?: boolean
    hideWishlistButton?: boolean
    showBackButton?: boolean
    searchRegionNode?: any
    showSearchbar?: boolean
    showNavigationLinks?: boolean
    megabar?: MegabarTransport
}

const HeaderSection: React.FC<Props> = ({
    isLoggedIn,
    firstName,
    url,
    headerSection,
    showVacationIdeasNavButton = true,
    isAllInputModalOpen,
    hideListRental = false,
    hideWishlistButton = false,
    showBackButton = true,
    searchRegionNode,
    showSearchbar = false,
    showNavigationLinks = true,
    megabar,
    onSearchIconClick,
}) => {
    const { isDesktop } = useContext(ScreenContext)
    const navigation = headerSection.navigationSectionResponse.navigation
    const { listRentalButton, logo, wishlistButton } = navigation

    const {
        isSidebarOpen,
        toggleSidebar,
        isUserSidebarOpen,
        toggleUserSidebar,
        popUpBoxVisible,
        togglePopUpBoxVisibility,
        listRentalClickHandler,
        wishlistClickHandler,
        wishlistCount,
        isLandlordRole,
        isGuestRole,
        inquiryCount,
    } = useLandingPageHeader({ url, listRentalButton })

    return (
        <StickyContainer>
            <Header id="header">
                <HeaderBar>
                    <HeaderBarContent>
                        <LeftSubContainer>
                            <LogoArea logo={logo} showBackButton={showBackButton} />
                            <SearchArea
                                searchRegionNode={searchRegionNode}
                                showSearchbar={showSearchbar}
                                isAllInputModalOpen={isAllInputModalOpen}
                                onSearchIconClick={onSearchIconClick}
                            />
                            <DesktopLayoutWrapper>
                                <NavigationLinks
                                    navigation={navigation}
                                    showVacationIdeasNavButton={showVacationIdeasNavButton}
                                    showNavigationLinks={showNavigationLinks}
                                />
                            </DesktopLayoutWrapper>
                        </LeftSubContainer>
                        <RightSubContainer>
                            <ListRentalWrapper>
                                {!isLoggedIn &&
                                    !hideListRental &&
                                    (isLoggedIn ? !isLandlordRole : true) &&
                                    !!listRentalButton?.url && (
                                        <GhostButtonWrapper>
                                            <SeoHref
                                                href={sanitizeUrlPath(listRentalButton.url)}
                                                noCrawl={listRentalButton.noCrawl ?? false}
                                            >
                                                <GhostButton
                                                    onClick={listRentalClickHandler}
                                                    padding={'0.3125rem'}
                                                    hoverEffect
                                                >
                                                    <GhostButtonContent>{listRentalButton.text}</GhostButtonContent>
                                                </GhostButton>
                                            </SeoHref>
                                        </GhostButtonWrapper>
                                    )}
                            </ListRentalWrapper>
                            <UserActionsContainer>
                                {!hideWishlistButton && (
                                    <WishlistButton
                                        wishlistButton={wishlistButton}
                                        wishlistCount={wishlistCount}
                                        onClick={wishlistClickHandler}
                                    />
                                )}
                                {!!onSearchIconClick && (
                                    <SearchIconContainer onClick={onSearchIconClick}>
                                        <IconImage as={SearchIcon as StyledSvgComponent} height={24} width={24} />
                                    </SearchIconContainer>
                                )}
                                <CompactLayoutWrapper>
                                    <UserMenuMobile
                                        navigation={navigation}
                                        isLoggedIn={isLoggedIn}
                                        toggleUserSidebar={toggleUserSidebar}
                                    />
                                </CompactLayoutWrapper>
                                <DesktopLayoutWrapper>
                                    <UserMenuDesktop
                                        navigation={navigation}
                                        isLoggedIn={isLoggedIn}
                                        firstName={firstName}
                                        isLandlordRole={isLandlordRole}
                                        isGuestRole={isGuestRole}
                                        inquiryCount={inquiryCount}
                                        popUpBoxVisible={popUpBoxVisible}
                                        togglePopUpBoxVisibility={togglePopUpBoxVisibility}
                                    />
                                </DesktopLayoutWrapper>
                                <MenuIconWrapper onClick={toggleSidebar}>
                                    <IconImage as={NavigationIcon as StyledSvgComponent} />
                                </MenuIconWrapper>
                            </UserActionsContainer>
                        </RightSubContainer>
                    </HeaderBarContent>
                </HeaderBar>
                <CompactLayoutWrapper>
                    <MenuDrawer
                        isLoggedIn={isLoggedIn}
                        open={isSidebarOpen && !isDesktop}
                        onClose={toggleSidebar}
                        headerSection={headerSection}
                        listRentalClickHandler={listRentalClickHandler}
                        showVacationIdeasNavButton={showVacationIdeasNavButton}
                        isLandlordRole={isLandlordRole}
                        isGuestRole={isGuestRole}
                        megabar={megabar}
                    />
                </CompactLayoutWrapper>
                {isLoggedIn && (
                    <CompactLayoutWrapper>
                        <ProfileDrawer
                            open={isUserSidebarOpen && !isDesktop}
                            onClose={toggleUserSidebar}
                            headerSection={headerSection}
                            isLandlordRole={isLandlordRole}
                            isGuestRole={isGuestRole}
                            firstName={firstName}
                            inquiryCount={inquiryCount}
                        />
                    </CompactLayoutWrapper>
                )}
            </Header>
        </StickyContainer>
    )
}

export default memo(HeaderSection)
